import { assertNever, type SiteUnconfirmed } from '@ev/shared'
import { useEffect } from 'react'
import { type Strings, useStrings } from '../../i18n'
import {
  CONFIRMED_AT,
  ensureFirestoreBackend,
  SETTLED_OVER_DAYS,
  standingFor,
  useConfirmationStore,
  useSiteConfirmation,
} from './store'

/**
 * What we could not settle, in the words of the finding.
 *
 * A `switch` closed by {@link assertNever} rather than a chain of ternaries. The
 * chain this replaces ended in the copy for a contested address, so a further
 * reason failed nowhere - it would have quietly told a reader that several
 * stations are listed here, which for a finding that means something else is
 * worse than saying nothing. `unlisted` is that further reason, and there will be
 * others: this does not compile until each has a case and both languages have
 * its copy.
 */
const explanationFor = (unconfirmed: SiteUnconfirmed, strings: Strings): string => {
  switch (unconfirmed.reason) {
    case 'address-contested':
      // With the count where the table gave us one: "3 different stations are
      // listed here" is something a reader standing in the car park can check,
      // and "several" is not.
      return unconfirmed.otherListings > 0
        ? strings.unconfirmed.addressContested(unconfirmed.otherListings)
        : strings.unconfirmed.reason['address-contested']
    case 'listing-retired':
      return strings.unconfirmed.reason['listing-retired']
    case 'unlisted':
      return strings.unconfirmed.reason.unlisted
    default:
      return assertNever(unconfirmed.reason, 'UnconfirmedReason')
  }
}

/**
 * The panel section for a site the pipeline could not confirm.
 *
 * Two jobs, and the order matters. First it says *what we could not settle*,
 * in the words of the actual finding rather than one hedge covering every
 * reason - "several stations are listed at this address" and "somebody reported
 * this one is gone" are different things to be told, and only the second is a
 * claim that the charger might be missing. Then it asks the one question a
 * person standing there can answer, and answering it is two buttons and no form.
 *
 * The second job is conditional on there being somewhere to keep the answer. With
 * no persistent backend the question, the buttons and the running score all go,
 * and the explanation is left to stand on its own - which it does. Asking anyway
 * would take a driver's word for it and drop it on reload without saying so, and
 * a tally of "0 so far" that nobody can move is worse than no tally.
 *
 * The score is shown as it stands rather than as a promise. A reader who answers
 * sees the number move; a reader who does not still learns how many others have.
 * Both of the things still missing are named - the weight of confirmation and the
 * days it has to be spread over - because a panel that says "one more and the
 * warning goes" and then does not is a panel that lied, and the day count is
 * exactly the condition an eager reader cannot satisfy by clicking again.
 */
export const UnconfirmedNotice = ({
  siteId,
  unconfirmed,
}: {
  siteId: string
  unconfirmed: SiteUnconfirmed
}) => {
  const strings = useStrings()
  const load = useConfirmationStore((state) => state.load)
  const cast = useConfirmationStore((state) => state.cast)
  const { score, confirmDays, goneDays, mine, pending, failed, rateLimited } =
    useSiteConfirmation(siteId)
  const persists = useConfirmationStore((state) => state.persists)

  useEffect(() => {
    // Nothing to show and nothing to fetch the SDK for: a score nobody can move
    // is not worth a round trip, let alone a third of the bundle.
    if (!persists) return
    // Upgrade before reading, so the first load already goes to Firestore where
    // it is configured. Both are idempotent; the swap happens at most once.
    ensureFirestoreBackend()
    void load(siteId)
  }, [siteId, load, persists])

  const standing = standingFor({ score, confirmDays, goneDays })
  // Cleared: enough people have said it is there, so the section stops warning
  // and says so instead of vanishing - the reader who just answered should see
  // what their answer did.
  if (standing === 'confirmed')
    return (
      <section className="ev-unconfirmed ev-unconfirmed--confirmed">
        <p className="ev-unconfirmed__resolved">{strings.unconfirmed.confirmed}</p>
        <p className="ev-unconfirmed__score">{strings.unconfirmed.score(score)}</p>
      </section>
    )

  // Both conditions are reported, both as what is still missing rather than as a
  // promise that answering once more settles it - it usually does not, and a
  // panel that says "one more and the warning goes" twice running has lied once.
  const remaining = CONFIRMED_AT - score
  const daysRemaining = SETTLED_OVER_DAYS - confirmDays

  return (
    <section className="ev-unconfirmed">
      <h3 className="ev-unconfirmed__heading">
        <span className="ev-unconfirmed__mark" aria-hidden="true">
          !
        </span>
        {standing === 'reported-gone'
          ? strings.unconfirmed.reportedGone
          : strings.unconfirmed.heading}
      </h3>
      <p className="ev-unconfirmed__explanation">{explanationFor(unconfirmed, strings)}</p>

      {/* The question, and everything that reports on answering it. Gated as
          one unit: buttons with no store behind them collect a driver's word
          and lose it, and a score beneath them would be reporting a tally
          nobody is keeping. */}
      {persists && (
        <>
          {/* A fieldset rather than a div with `role="group"`: the question is the
              legend for the two answers, which is the semantic the markup already
              meant and which needs no `aria-labelledby` to wire up. */}
          <fieldset className="ev-unconfirmed__fieldset">
            <legend className="ev-unconfirmed__question">{strings.unconfirmed.question}</legend>
            <div className="ev-unconfirmed__answers">
              <button
                type="button"
                className="ev-unconfirmed__answer ev-unconfirmed__answer--yes"
                aria-pressed={mine === 1}
                disabled={pending}
                onClick={() => {
                  void cast(siteId, 1)
                }}
              >
                {strings.unconfirmed.yes}
              </button>
              <button
                type="button"
                className="ev-unconfirmed__answer ev-unconfirmed__answer--no"
                aria-pressed={mine === -1}
                disabled={pending}
                onClick={() => {
                  void cast(siteId, -1)
                }}
              >
                {strings.unconfirmed.no}
              </button>
            </div>
          </fieldset>

          <p className="ev-unconfirmed__score">
            {strings.unconfirmed.score(score)}
            {remaining > 0 && score > 0 && ` · ${strings.unconfirmed.toGo(remaining)}`}
            {daysRemaining > 0 && score > 0 && ` · ${strings.unconfirmed.daysToGo(daysRemaining)}`}
          </p>

          {/* Both are live regions: the answer lands without moving focus, so a
              screen reader has to be told rather than left to discover it. */}
          <p className="ev-unconfirmed__status" role="status">
            {pending ? strings.unconfirmed.voting : mine !== null && strings.unconfirmed.thanks}
          </p>
          {/* One region, two things it can say. A vote refused by the hourly limit is
              not a vote that failed to send, and telling somebody to try again when
              trying again cannot work is worse than saying nothing. */}
          {(failed || rateLimited) && (
            <p className="ev-unconfirmed__failed" role="alert">
              {rateLimited ? strings.unconfirmed.rateLimited : strings.unconfirmed.failed}
            </p>
          )}
        </>
      )}
    </section>
  )
}
